import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articles.find((a) => a.id === articleId);

  useEffect(() => {
    if (!article) return;

    const catInfo = categories.find((c) => c.slug === article.category);

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.publishDate,
      articleSection: catInfo?.name || article.category,
      keywords: article.tags.join(', '),
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '海角论坛',
          item: 'https://haijiaoluntan.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '版块分类',
          item: 'https://haijiaoluntan.com/category',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: catInfo?.name || article.category,
          item: `https://haijiaoluntan.com/category/${article.category}`,
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: article.title,
        },
      ],
    };

    const existingArticle = document.getElementById('article-schema');
    const existingBreadcrumb = document.getElementById('article-breadcrumb-schema');
    if (existingArticle) existingArticle.remove();
    if (existingBreadcrumb) existingBreadcrumb.remove();

    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.id = 'article-schema';
    articleScript.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(articleScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'article-breadcrumb-schema';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      const a = document.getElementById('article-schema');
      const b = document.getElementById('article-breadcrumb-schema');
      if (a) a.remove();
      if (b) b.remove();
    };
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章已被删除或链接失效</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catInfo = categories.find((c) => c.slug === article.category);
  const relatedPosts = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '版块分类', href: '/category' },
            { label: catInfo?.name || article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                {article.isPinned && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-accent-100 text-accent-600 rounded-full">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-pushpin-line text-[10px]"></i>
                    </div>
                    置顶
                  </span>
                )}
                {article.isHot && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-600 rounded-full">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-fire-line text-[10px]"></i>
                    </div>
                    热门
                  </span>
                )}
                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-secondary-100 text-secondary-600 rounded-full">
                  {catInfo?.name || article.category}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-full h-full object-cover"
                      width={32}
                      height={32}
                    />
                  </div>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span>发布于 {article.publishDate}</span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-xs"></i>
                  </div>
                  {article.views.toLocaleString()} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-chat-3-line text-xs"></i>
                  </div>
                  {article.replies} 回复
                </span>
              </div>
            </div>

            <div
              className="prose prose-base max-w-none mb-12 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-10">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-6 border-t border-b border-background-200 mb-12">
              <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-heart-line text-sm"></i>
                </div>
                点赞 ({article.likes.toLocaleString()})
              </button>
              <div className="flex items-center gap-3">
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors duration-200 cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors duration-200 cursor-pointer">
                  <i className="ri-bookmark-line"></i>
                </button>
              </div>
            </div>

            {relatedPosts.length > 0 && (
              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/article/${post.id}`}
                      className="block p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer group"
                    >
                      <h3 className="text-sm font-medium text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {post.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{post.author}</span>
                        <span>{post.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}